import type {
  Agent, Tool, Skill, Platform, AgentSchedule, AgentRunLog, JobBoardRequisition, PanelUser,
} from '@/types'

// Datos de ejemplo — reflejan agentes reales de Connect para que las pantallas
// se sientan reales desde el día 1. Reemplazar por datos del backend cuando conecte.

export const mockAgents: Agent[] = [
  {
    id: 'antonio-mmm-confirmacion',
    client_id: 'connect',
    name: 'Antonio — Confirmación MMM PR',
    description: 'Agente de voz que confirma citas de asistencia vial vía llamada saliente.',
    channel: 'voice',
    status: 'active',
    owner: 'George Marín',
    monthly_budget_usd: 120,
    tech_stack: ['ElevenLabs', 'Twilio', 'Azure Functions'],
    metrics_table: 'cx_calls',
    model: 'eleven-turbo-v2',
    prompt_version: 7,
    created_at: '2026-03-01T00:00:00Z',
    updated_at: '2026-07-20T00:00:00Z',
  },
  {
    id: 'soda-cx-inbound',
    client_id: 'connect',
    name: 'SODA CX — Inbound',
    description: 'Agente inbound de atención al cliente. ~38% de resolución sin escalar.',
    channel: 'voice',
    status: 'active',
    owner: 'George Marín',
    monthly_budget_usd: 200,
    tech_stack: ['ElevenLabs', 'Twilio'],
    metrics_table: 'cx_calls',
    model: 'eleven-turbo-v2',
    prompt_version: 12,
    created_at: '2026-02-10T00:00:00Z',
    updated_at: '2026-07-28T00:00:00Z',
  },
  {
    id: 'kai-academy-assistant',
    client_id: 'connect',
    name: 'KAI Academy — Asistente',
    description: 'Asistente interno de formación para agentes de Connect vía WebRTC.',
    channel: 'internal',
    status: 'active',
    owner: 'Daya',
    monthly_budget_usd: 60,
    tech_stack: ['ElevenLabs WebRTC', 'Firebase Auth', 'Supabase RBAC'],
    metrics_table: 'agent_runs',
    model: 'claude-sonnet-4-5',
    prompt_version: 3,
    created_at: '2026-04-15T00:00:00Z',
    updated_at: '2026-07-30T00:00:00Z',
  },
]

export const mockTools: Tool[] = [
  { id: 'el-transfer-call', name: 'Transfer Call', description: 'Transfiere la llamada activa a un número de destino.', tool_type: 'acción', category: 'voice', provider: 'elevenlabs', lives_on: 'elevenlabs', auth_method: 'native', scopes_available: ['execute'] },
  { id: 'el-end-call', name: 'End Call', description: 'Finaliza la llamada activa de forma controlada.', tool_type: 'acción', category: 'voice', provider: 'elevenlabs', lives_on: 'elevenlabs', auth_method: 'native', scopes_available: ['execute'] },
  { id: 'az-customer-lookup', name: 'Customer Lookup', description: 'Consulta datos del cliente en Helios por número de teléfono.', tool_type: 'acción', category: 'crm', provider: 'custom', lives_on: 'azure-functions', auth_method: 'webhook_secret', scopes_available: ['read_customer'] },
  { id: 'az-appointment-confirm', name: 'Confirm Appointment', description: 'Marca una cita como confirmada en el sistema de dispatch.', tool_type: 'acción', category: 'crm', provider: 'custom', lives_on: 'azure-functions', auth_method: 'webhook_secret', scopes_available: ['update_appointment'] },
  { id: 'az-sms-notify', name: 'Send SMS', description: 'Envía SMS de confirmación vía Twilio.', tool_type: 'integración', category: 'voice', provider: 'twilio', lives_on: 'azure-functions', auth_method: 'api_key', scopes_available: ['send'] },
  { id: 'supabase-metrics-write', name: 'Write Call Metrics', description: 'Persiste métricas de la llamada al finalizar.', tool_type: 'integración', category: 'observability', provider: 'supabase', lives_on: 'azure-functions', auth_method: 'service_role', scopes_available: ['write'] },
]

export const mockSkills: Skill[] = [
  { id: 'confirmacion-cita-vial', name: 'Confirmación de cita vial', skill_category: 'conversacional', version: '1.3', description: 'Cómo confirmar, reagendar o cancelar una cita de asistencia vial.', updated_at: '2026-07-15T00:00:00Z' },
  { id: 'manejo-escalacion-cx', name: 'Manejo de escalación CX', skill_category: 'conversacional', version: '2.0', description: 'Cuándo y cómo transferir a un agente humano.', updated_at: '2026-07-22T00:00:00Z' },
  { id: 'kai-onboarding-flujo', name: 'Flujo de onboarding KAI', skill_category: 'operacional', version: '1.0', description: 'Guía paso a paso para nuevos agentes en formación.', updated_at: '2026-06-30T00:00:00Z' },
]

export const mockPlatforms: Platform[] = [
  { id: 'platform-elevenlabs', name: 'ElevenLabs', platform_type: 'plataforma', provider: 'elevenlabs', status: 'active', description: 'Runtime de voz para Antonio y SODA CX.' },
  { id: 'platform-twilio', name: 'Twilio', platform_type: 'plataforma', provider: 'twilio', status: 'active', description: 'Telefonía — números y SIP.' },
  { id: 'platform-azure-functions', name: 'Azure Functions', platform_type: 'plataforma', provider: 'microsoft', status: 'active', description: 'Cómputo serverless — tools y orquestación.' },
  { id: 'platform-supabase', name: 'Supabase', platform_type: 'base_de_datos', provider: 'supabase', status: 'active', description: 'Config store y RBAC (connect-auth-api).' },
  { id: 'platform-firebase', name: 'Firebase Auth', platform_type: 'plataforma', provider: 'google', status: 'active', description: 'Autenticación — Google SSO, allowlist @connect.inc.' },
]

export const mockSchedules: AgentSchedule[] = [
  { id: 'sch-1', agent_id: 'antonio-mmm-confirmacion', name: 'Confirmaciones diarias', description: 'Corre la ronda de confirmación de citas del día.', cron_expression: '0 8 * * *', prompt: 'Confirmar todas las citas agendadas para hoy', channel: 'slack', enabled: true, last_status: 'ok', run_count: 143 },
]

export const mockRunLogs: AgentRunLog[] = [
  { id: 'run-1', agent_id: 'antonio-mmm-confirmacion', run_type: 'schedule', run_at: '2026-08-04T08:00:00Z', status: 'ok', model: 'eleven-turbo-v2', tokens_used: 1200, duration_ms: 4300 },
  { id: 'run-2', agent_id: 'antonio-mmm-confirmacion', run_type: 'manual', run_at: '2026-08-03T14:20:00Z', status: 'error', model: 'eleven-turbo-v2', tokens_used: 400, duration_ms: 1100 },
]

export const mockJobBoard: JobBoardRequisition[] = [
  { id: 'jd-1', agent_name: 'Agente de seguimiento de siniestros', status: 'en_revision', owner: 'George Marín', created_at: '2026-07-25T00:00:00Z' },
  { id: 'jd-2', agent_name: 'Agente de reportes M3 automatizado', status: 'borrador', owner: 'George Marín', created_at: '2026-08-01T00:00:00Z' },
]

export const mockUsers: PanelUser[] = [
  { email: 'george@connect.inc', role: 'admin', created_at: '2026-03-01T00:00:00Z' },
  { email: 'daya@connect.inc', role: 'analyst', created_at: '2026-04-10T00:00:00Z' },
]
